import {
  addLineModeAtom,
  DEFAULT_ADD_LINE_MODE,
  meshPickTargetsAtom,
  pointCloudTargetsAtom,
} from '@/atoms/viewer.atom';
import { CrosshairMarker } from '@/components/3D/CrosshairMarker';
import { pickSurface } from '@/components/3D/pickSurface';
import { SegmentLabels } from '@/components/3D/SegmentLabels';
import { TrailingSegmentLine } from '@/components/3D/TrailingSegmentLine';
import { Line } from '@react-three/drei';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

const LINE_COLOR = '#10b981';
const LINE_COLOR_PREVIOUS_PART = '#10b98199';

type VertexAddress = { kind: 'current'; vertexIdx: number } | { kind: 'committed'; partIdx: number; vertexIdx: number };

/**
 * In-canvas N-point picker for LineString / MultiLineString features.
 *
 * Each click on a point cloud or mesh appends a vertex to `currentPart`.
 * "+ Part" (handled by the toolbar) closes the current stroke and starts
 * a new one. Finalize transitions to the `adjusting` phase, where vertex
 * markers stay draggable so the user can refine positions before saving.
 */
export const AddLineTool = () => {
  const [mode, setMode] = useAtom(addLineModeAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);

  const enabled = mode.enabled;
  const isPicking = enabled && mode.phase === 'picking';

  // Drag state for vertex repositioning. justDraggedRef matches AddPointTool's
  // pattern: when a drag completes, swallow the next pointerup so we don't
  // accidentally append a new vertex in addition to moving the dragged one.
  const draggingRef = useRef<{ address: VertexAddress; plane: THREE.Plane } | null>(null);
  // After a vertex drag / marker click resolves, we suppress the next
  // canvas click that arrives within a short window so the same gesture
  // doesn't both move-or-finalize a vertex and append a new one.
  // Timestamp-based instead of a boolean flag so we don't get stuck
  // suppressing forever if the trailing canvas click never arrives
  // (R3F event capture and browser timing both make that possible).
  const suppressClickUntilRef = useRef(0);
  // Latest snapshots of mutable inputs the click handler needs. Stored
  // in refs so the click effect re-binds only on the isPicking flip,
  // never during a gesture.
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const pcoTargetsRef = useRef(pcoTargets);
  pcoTargetsRef.current = pcoTargets;
  const meshTargetsRef = useRef(meshTargets);
  meshTargetsRef.current = meshTargets;

  // Reset cursor when tool is disabled.
  useEffect(() => {
    if (!enabled) {
      renderer.domElement.style.cursor = 'default';
      setHoverPoint(null);
    }
  }, [enabled, renderer]);

  // Hover preview during picking.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;

    const onMove = async (e: MouseEvent) => {
      if (draggingRef.current) return;
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(
        renderer,
        camera,
        raycaster,
        pcoTargetsRef.current,
        meshTargetsRef.current,
      );
      if (hit) {
        setHoverPoint(hit.position.clone());
        dom.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        dom.style.cursor = 'default';
      }
    };

    dom.addEventListener('pointermove', onMove);
    return () => {
      dom.removeEventListener('pointermove', onMove);
      dom.style.cursor = 'default';
    };
    // Pickable targets are read through refs so a layer-visibility
    // toggle doesn't re-bind the listener mid-hover.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking]);

  // Click-to-pick handler. Uses the browser-level `click` event
  // (atomic — only fires when pointerdown + pointerup happen on the
  // same element with no significant move) so we don't have to track
  // pointer state across event-handler invocations. The previous
  // pointerdown/pointerup pair was the source of the "first click
  // sometimes doesn't register" bug: any state change in between
  // wiped the listener mid-gesture.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;

    const onClick = async (e: MouseEvent) => {
      if (e.button !== 0) return;
      // Just finished dragging a vertex — don't also append a new one.
      if (Date.now() < suppressClickUntilRef.current) return;
      const rect = dom.getBoundingClientRect();
      // Read latest mode via ref — the effect intentionally doesn't
      // depend on `mode.currentPart` so it never re-binds mid-gesture.
      const m = modeRef.current;

      // Snap-to-first detection at click time. Done BEFORE the
      // `draggingRef` bail because R3F's marker pointerdown sets
      // `draggingRef` and that handler's own onUp fires AFTER the
      // canvas pointerup (bubble order: canvas → window).
      //
      // For lines, snap-to-first means *close the loop*: append the
      // first vertex's coordinate to the end of the part so the
      // resulting LineString visits v0, v1, …, vN, v0. The polygon
      // tool already closes its rings at persist time, but the line
      // tool's geometry is open by default — without this append the
      // user's click would just finalize the polyline without
      // actually drawing the closing edge they asked for.
      if (m.currentPart.length >= 2) {
        const first = m.currentPart[0];
        const firstVec = new THREE.Vector3(first[0], first[1], first[2]);
        const ndc = firstVec.clone().project(camera);
        if (ndc.z >= -1 && ndc.z <= 1) {
          const fx = ((ndc.x + 1) / 2) * rect.width;
          const fy = ((1 - ndc.y) / 2) * rect.height;
          const ddx = e.clientX - rect.left - fx;
          const ddy = e.clientY - rect.top - fy;
          if (Math.sqrt(ddx * ddx + ddy * ddy) <= 14) {
            draggingRef.current = null;
            // Stay in picking — committing a closed loop should NOT
            // jump straight to adjusting. The user might want to keep
            // adding more parts (multi-line). They commit when ready
            // via Finalize / Enter / dblclick.
            setMode((prev) => {
              if (prev.currentPart.length < 2) return prev;
              const closedPart: [number, number, number][] = [...prev.currentPart, prev.currentPart[0]];
              return {
                ...prev,
                parts: [...prev.parts, closedPart],
                currentPart: [],
              };
            });
            setHoverPoint(null);
            return;
          }
        }
      }

      if (draggingRef.current) return;

      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(
        renderer,
        camera,
        raycaster,
        pcoTargetsRef.current,
        meshTargetsRef.current,
      );
      if (!hit) return;
      const p: [number, number, number] = [hit.position.x, hit.position.y, hit.position.z];
      setMode((prev) => ({ ...prev, currentPart: [...prev.currentPart, p] }));
      setHoverPoint(null);
    };

    dom.addEventListener('click', onClick);
    return () => dom.removeEventListener('click', onClick);
    // Click effect re-binds ONLY when isPicking flips. All mutable
    // state the handlers need (mode, pcoTargets, meshTargets) is read
    // through refs so the listener stays bound across the entire
    // tool session — eliminates every race window where a re-bind
    // between pointerdown and pointerup could swallow the first click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking]);

  // Cancel via right-click during picking.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;
    const onCtx = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMode({ ...DEFAULT_ADD_LINE_MODE });
    };
    dom.addEventListener('contextmenu', onCtx);
    return () => dom.removeEventListener('contextmenu', onCtx);
  }, [isPicking, renderer, setMode]);

  // Double-click to finalize: commit the current part (if it has ≥2 verts)
  // along with all previously-committed parts and transition to adjusting.
  // Mirrors AddLineToolbar.handleFinalize but lives here so it works
  // regardless of where the cursor is when the user double-clicks.
  useEffect(() => {
    if (!isPicking) return;
    const dom = renderer.domElement;
    const onDbl = (e: MouseEvent) => {
      e.preventDefault();
      setMode((prev) => {
        if (prev.phase !== 'picking') return prev;
        const validCurrent = prev.currentPart.length >= 2;
        const allParts = validCurrent ? [...prev.parts, prev.currentPart] : prev.parts;
        if (allParts.length === 0) return prev;
        return { ...prev, parts: allParts, currentPart: [], phase: 'adjusting' };
      });
    };
    dom.addEventListener('dblclick', onDbl);
    return () => dom.removeEventListener('dblclick', onDbl);
  }, [isPicking, renderer, setMode]);

  /**
   * Vertex drag — same camera-perpendicular plane as AddPointTool's draft
   * drag. Click-vs-drag is decided by a 5 px movement threshold so a
   * pure click on a vertex marker is absorbed (doesn't append a new
   * vertex on the canvas behind it) and only an actual drag moves the
   * vertex. On release, snaps to the picked surface.
   */
  const startVertexDrag = (e: ThreeEvent<PointerEvent>, address: VertexAddress) => {
    if (!enabled) return;
    e.stopPropagation();

    const camDir = camera.getWorldDirection(new THREE.Vector3());
    const point = e.point.clone();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(camDir, point);
    draggingRef.current = { address, plane };
    const startX = e.clientX;
    const startY = e.clientY;
    const DRAG_THRESHOLD_PX = 5;
    let didDrag = false;

    const dom = renderer.domElement;

    const writeVertex = (next: [number, number, number]) => {
      setMode((prev) => {
        if (address.kind === 'current') {
          if (address.vertexIdx >= prev.currentPart.length) return prev;
          const updated = prev.currentPart.slice();
          updated[address.vertexIdx] = next;
          return { ...prev, currentPart: updated };
        }
        if (address.partIdx >= prev.parts.length) return prev;
        const part = prev.parts[address.partIdx];
        if (address.vertexIdx >= part.length) return prev;
        const updatedPart = part.slice();
        updatedPart[address.vertexIdx] = next;
        const updatedParts = prev.parts.slice();
        updatedParts[address.partIdx] = updatedPart;
        return { ...prev, parts: updatedParts };
      });
    };

    const onMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      if (!didDrag) {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (Math.sqrt(dx * dx + dy * dy) <= DRAG_THRESHOLD_PX) return;
        didDrag = true;
      }
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = new THREE.Vector3();
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      writeVertex([hit.x, hit.y, hit.z]);
    };

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp);
      draggingRef.current = null;
      // Always suppress the canvas click that follows — even a pure
      // click on a vertex shouldn't drop a new vertex underneath.
      suppressClickUntilRef.current = Date.now() + 200;
      if (!didDrag) {
        // Pure click on the first vertex of the current part → close
        // the line into a loop. Stay in picking — the user might want
        // to add more parts (multi-line). They commit explicitly via
        // Finalize / Enter / dblclick.
        if (address.kind === 'current' && address.vertexIdx === 0) {
          setMode((prev) => {
            if (prev.currentPart.length < 2) return prev;
            const closedPart: [number, number, number][] = [...prev.currentPart, prev.currentPart[0]];
            return {
              ...prev,
              parts: [...prev.parts, closedPart],
              currentPart: [],
            };
          });
        }
        return;
      }

      // Snap to the surface under the final cursor position when possible.
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      if (hit) writeVertex([hit.position.x, hit.position.y, hit.position.z]);
    };

    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp);
  };

  if (!enabled) return null;

  // Floating origin: render everything relative to a stable session
  // origin so we don't push UTM-scale (millions of metres) coordinates
  // through the GPU's float32 pipeline — that's what was causing the
  // visible jitter when orbiting the camera. Origin = first picked
  // vertex when available, else (0, 0, 0). Once a vertex is picked the
  // origin is stable for the rest of the session (undo can shift it but
  // that's a rare action). The parent `<group position={origin}>` then
  // adds the offset back at world transform time using the matrix
  // (one-shot translation, not per-vertex).
  const origin: [number, number, number] =
    mode.currentPart.length > 0
      ? mode.currentPart[0]
      : mode.parts.length > 0 && mode.parts[0].length > 0
        ? mode.parts[0][0]
        : [0, 0, 0];
  const sub = (x: number, y: number, z: number): THREE.Vector3 =>
    new THREE.Vector3(x - origin[0], y - origin[1], z - origin[2]);
  const subVec = (v: THREE.Vector3): THREE.Vector3 => sub(v.x, v.y, v.z);

  const previousPartLines = mode.parts.map((p) => p.map(([x, y, z]) => sub(x, y, z)));
  const currentVerts = mode.currentPart.map(([x, y, z]) => sub(x, y, z));
  const hoverRel = hoverPoint ? subVec(hoverPoint) : null;
  // Trailing dashed segment from last vertex to cursor — its own Line so
  // the dash pattern starts at distance 0 (extending the committed line
  // would make short hover segments fall in a gap and disappear).
  const trailingSegment: [THREE.Vector3, THREE.Vector3] | null =
    isPicking && hoverRel && currentVerts.length >= 1 ? [currentVerts[currentVerts.length - 1], hoverRel] : null;
  const committedCurrent = currentVerts.length >= 2 ? currentVerts : null;
  const previousColor = isPicking ? LINE_COLOR_PREVIOUS_PART : LINE_COLOR;
  const previousOpacity = isPicking ? 0.7 : 0.95;

  // SegmentLabels operates on raw arrays — pass it origin-relative
  // coordinates and a relative cursor so its midpoint positions are
  // also inside this floating-origin group's local space.
  const partsRel: [number, number, number][][] = mode.parts.map((p) =>
    p.map(([x, y, z]) => [x - origin[0], y - origin[1], z - origin[2]] as [number, number, number]),
  );
  const currentPartRel: [number, number, number][] = mode.currentPart.map(
    ([x, y, z]) => [x - origin[0], y - origin[1], z - origin[2]] as [number, number, number],
  );

  return (
    <group position={origin}>
      {previousPartLines.map((pts, i) =>
        pts.length >= 2 ? (
          <Line
            key={`line-prev-${i}`}
            points={pts}
            color={previousColor}
            lineWidth={isPicking ? 2.5 : 3}
            transparent
            opacity={previousOpacity}
            depthTest={false}
          />
        ) : null,
      )}

      {/* Vertices for committed parts — draggable, slightly smaller. */}
      {previousPartLines.map((pts, partIdx) =>
        pts.map((v, vertexIdx) => (
          <CrosshairMarker
            key={`prev-vert-${partIdx}-${vertexIdx}`}
            position={v}
            color={LINE_COLOR}
            screenSize={0.012}
            opacity={isPicking ? 0.6 : 0.9}
            onPointerDown={(e) => startVertexDrag(e, { kind: 'committed', partIdx, vertexIdx })}
          />
        )),
      )}

      {/* Vertices for the in-progress current part — draggable. */}
      {currentVerts.map((v, vertexIdx) => (
        <CrosshairMarker
          key={`cur-vert-${vertexIdx}`}
          position={v}
          color={LINE_COLOR}
          screenSize={0.014}
          onPointerDown={(e) => startVertexDrag(e, { kind: 'current', vertexIdx })}
        />
      ))}

      {committedCurrent && (
        <Line points={committedCurrent} color={LINE_COLOR} lineWidth={3} transparent opacity={0.95} depthTest={false} />
      )}

      {trailingSegment && <TrailingSegmentLine points={trailingSegment} color={LINE_COLOR} />}

      {/* Cursor preview marker — same look as AddPointTool's hover. */}
      {isPicking && hoverRel && !draggingRef.current && (
        <CrosshairMarker position={hoverRel} color={LINE_COLOR} screenSize={0.012} opacity={0.55} sphereOpacity={0.4} />
      )}

      {/* Per-segment length labels at each midpoint (origin-relative). */}
      <SegmentLabels parts={partsRel} current={currentPartRel} cursor={isPicking ? hoverRel : null} />
    </group>
  );
};
